import { createRoot } from 'react-dom/client'
import './styles.css'
import { Logo } from '@pmndrs/branding'
import { App } from './App'
import { create } from 'zustand'
import { motion, animate } from 'motion'
import { useRef, useEffect, useState } from 'react'
import Confetti from 'react-confetti'

/*
Model JSX auto-generated by: https://github.com/pmndrs/gltfjsx
Command: npx gltfjsx@6.2.3 model.glb --transform --simplify --resolution=2048
Author: Omar Faruq Tawsif (https://sketchfab.com/omarfaruqtawsif32)
License: CC-BY-4.0 (http://creativecommons.org/licenses/by/4.0/)
*/

// Zustand store for managing score
export const useStore = create((set) => ({
  score: 0,
  clickedCards: new Set(),
  showBanner: true,
  showBannerButton: true,
  incrementScore: (imageName, score) =>
    set((state) => {
      if (!state.clickedCards.has(imageName)) {
        const newClickedCards = new Set(state.clickedCards)
        newClickedCards.add(imageName)
        return { score: state.score + score, clickedCards: newClickedCards }
      }
      return state
    }),
  hideBanner: () => set({ showBanner: false }),
  hideButton: () => set({ showBannerButton: false })
}))

// ScoreIndicator component
function ScoreIndicator() {
  const score = useStore((state) => state.score)
  const scoreRef = useRef(null)
  const prevScore = useRef(score)
  const [showConfetti, setShowConfetti] = useState(false)
  const [windowDimensions, setWindowDimensions] = useState({
    width: window.innerWidth,
    height: window.innerHeight
  })

  // Update window dimensions when window resizes
  useEffect(() => {
    const handleResize = () => {
      setWindowDimensions({
        width: window.innerWidth,
        height: window.innerHeight
      })
    }

    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  // Determine score status and color
  const getScoreStatus = (score) => {
    if (score <= 25) return { status: 'Grey', color: '#6B7280' }
    if (score <= 45) return { status: 'Red', color: '#EF4444' }
    return { status: 'Yellow', color: '#F59E0B' }
  }

  const { status, color } = getScoreStatus(score)

  // Animate score when it changes
  useEffect(() => {
    if (scoreRef.current && prevScore.current !== score) {
      animate(scoreRef.current, { opacity: [1, 0.8, 1], scale: [1, 1.2, 1] }, { duration: 0.5, easing: 'ease-out' })

      // Animate the number counting up
      const from = prevScore.current
      const to = score

      const controls = animate(from, to, {
        duration: 0.8,
        easing: 'ease-out',
        onUpdate: (value) => {
          if (scoreRef.current) {
            scoreRef.current.textContent = Math.round(value)
          }
        }
      })

      // Check if we've crossed the yellow threshold (from ≤45 to >45)
      if (prevScore.current <= 45 && score > 45) {
        setShowConfetti(true)
      }

      prevScore.current = score
      return () => controls.stop()
    }
  }, [score])

  return (
    <>
      {showConfetti && (
        <Confetti width={windowDimensions.width} height={windowDimensions.height} recycle={false} numberOfPieces={500} gravity={0.3} initialVelocityY={10} />
      )}
      <div className="score-indicator">
        <div className="score-container">
          <div className="score-value" ref={scoreRef}>
            {score}
          </div>
          <div className="score-label">SCORE</div>
        </div>
        <div className="score-status" style={{ backgroundColor: color }}>
          {status}
        </div>
      </div>
    </>
  )
}

// BannerButton component
function BannerButton() {
  const showBannerButton = useStore((state) => state.showBannerButton)
  const hideBanner = useStore((state) => state.hideBanner)
  const hideButton = useStore((state) => state.hideButton)
  const buttonRef = useRef(null)
  const [isAnimatingOut, setIsAnimatingOut] = useState(false)

  const handleClick = () => {
    // Immediately hide the banner
    hideBanner()

    // Start button fade out animation
    setIsAnimatingOut(true)

    // Remove button from DOM after animation completes
    setTimeout(() => {
      hideButton()
    }, 800) // Match the CSS transition duration
  }

  // Don't render if button should be hidden
  if (!showBannerButton) return null

  return (
    <div
      ref={buttonRef}
      className="banner-button"
      onClick={handleClick}
      style={{
        opacity: isAnimatingOut ? 0 : 1,
        transition: 'opacity 0.8s ease-out, transform 0.2s ease'
      }}>
      Remove Banner
    </div>
  )
}

function Root() {
  return (
    <>
      <App />
      <div style={{ position: 'absolute', pointerEvents: 'none', top: 0, left: 0, width: '100vw', height: '100vh' }}>
        <ScoreIndicator />
        <div style={{ position: 'absolute', bottom: 40, right: 40 }}>
          <div style={{ marginBottom: 4, fontSize: '1.25rem', fontWeight: 600 }}>BNI Clerkenwell</div>
          <div style={{ fontSize: '0.875rem' }}>Thursday 6th March 2025</div>
        </div>
        <BannerButton />
        <img src="PalaLogoNew.png" style={{ position: 'absolute', bottom: 40, left: 40, height: 50 }} />
        <div 
          style={{ 
            position: 'absolute', 
            top: 20, 
            left: 40,
            backgroundColor: 'white',
            borderRadius: '12px',
            padding: '12px 20px',
            paddingLeft: '28px',
            boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
            backdropFilter: 'blur(5px)',
            minWidth: '350px',
            maxWidth: '450px',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden' /* Ensures the accent doesn't overflow */
          }}
        >
          {/* Red accent on the left side */}
          <div 
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              bottom: 0,
              width: '12px',
              backgroundColor: '#D11C2F',
            }}
          />
          <div 
            style={{ 
              fontSize: '1.5rem', 
              fontWeight: 700,
              color: '#333',
              marginBottom: '4px',
              position: 'relative' /* Ensures text is above the accent */
            }}
          >
            Jim’s Carousel of (Moderate) BNI Success
          </div>
          <div style={{ 
            color: 'rgba(0, 0, 0, 0.6)',
            fontSize: '0.875rem',
            fontWeight: '500',
            position: 'relative' /* Ensures text is above the accent */
          }}>
            Getting to yellow in your first three months!
          </div>
        </div>
      </div>{' '}
    </>
  )
}

createRoot(document.getElementById('root')).render(<Root />)
